import { createGrid } from "ag-grid-community";
import { Controller } from "@hotwired/stimulus";
import { spreadsheetTheme } from '../ag_grid/theme';

// Connects to data-controller="file-preview"
//
// Shows the uploaded spreadsheet as cells - column letters across the top, real
// spreadsheet row numbers down the side - so the user can pick which sheet to
// import and mark the corners of their table.
//
// This step exists because a workbook has no single obvious table. Given no
// sheet, DuckDB reads the first one; given no range, it guesses a shape. On a
// sheet with a title row above the data that produces one nonsense column and
// zero rows, with no error at all. Showing the actual cells is the only way the
// user can tell us what to read.
//
// Both corners can be clicked on the grid or typed in. Typing is not a
// convenience: the bottom-right of a table is frequently past the 50 rows the
// preview shows, and there is nothing to click on.
export default class extends Controller {
  static targets = [
    "panel", "sheetSelect", "grid", "summary", "hint",
    "warning", "warningMessage", "error", "errorMessage",
    "continueButton", "cornerHint", "startCellField", "endCellField", "expandRowsCheckbox",
    "sheetInput", "startCellInput", "endColumnInput", "endRowInput", "formatInput"
  ]

  static values = {
    previewUrl: String,
    s3Key: String,
    filename: String,
    // When set, Continue navigates here with the selection in the query string
    // instead of submitting a form. That is how the mapping-preview page works:
    // the file is already in S3, so the choice is made, the page reloads, and
    // the server probes the schema of the chosen range.
    continueUrl: String,
    // True when the file is already uploaded and the grid should load on page
    // load rather than waiting for an upload event.
    autoload: Boolean
  }

  // A1-style reference, matching CellRef::CELL_PATTERN on the server.
  static CELL_PATTERN = /^[A-Z]{1,3}[1-9]\d{0,6}$/

  connect() {
    this.grid = null
    this.selection = null
    // Which corner a click on the grid sets. Starts at the top-left, because
    // that is the one nothing can be inferred without.
    this.activeCorner = 'start'
    // The params the grid currently reflects, so a change event and the Enter
    // key that precedes it do not fire the same request twice.
    this.appliedParams = null
    this.renderActiveCorner()

    if (this.autoloadValue && this.s3KeyValue) {
      this.s3Key = this.s3KeyValue
      this.filename = this.filenameValue
      this.previewUrl = this.previewUrlValue
      this.panelTarget.classList.remove('hidden')
      this.reload()
    }
  }

  disconnect() {
    if (this.grid) {
      this.grid.destroy()
      this.grid = null
    }
  }

  // Entry point: fired by the upload controller once the file is in S3.
  render(event) {
    const { preview, s3Key, filename, previewUrl } = event.detail
    this.s3Key = s3Key
    this.filename = filename
    this.previewUrl = previewUrl

    this.panelTarget.classList.remove('hidden')
    this.renderSheets(preview)
    this.applyPreview(preview)
  }

  renderSheets(preview) {
    const select = this.sheetSelectTarget
    select.innerHTML = ''

    preview.sheets.forEach((sheet) => {
      const option = document.createElement('option')
      option.value = sheet.name
      // Hidden sheets are still selectable - they read fine - but the user
      // should understand why one looks unfamiliar.
      option.textContent = sheet.hidden ? `${sheet.name} (hidden)` : sheet.name
      option.selected = sheet.name === preview.sheet
      select.appendChild(option)
    })
  }

  // Changing sheet invalidates both corners: the same references mean something
  // different, or nothing at all, on another sheet.
  async sheetChanged() {
    this.selection = null
    this.startCellFieldTarget.value = ''
    this.endCellFieldTarget.value = ''
    this.setActiveCorner('start')
    this.setHidden(null)
    await this.reload()
  }

  activateStart() {
    this.setActiveCorner('start')
  }

  activateEnd() {
    this.setActiveCorner('end')
  }

  setActiveCorner(corner) {
    this.activeCorner = corner
    this.renderActiveCorner()
  }

  renderActiveCorner() {
    const active = this.activeCorner === 'end' ? 'bottom-right' : 'top-left'
    this.cornerHintTarget.textContent = `Clicking a cell sets the ${active} corner.`

    const ring = ['ring-2', 'ring-primary-500']
    this.startCellFieldTarget.classList.toggle(ring[0], this.activeCorner === 'start')
    this.startCellFieldTarget.classList.toggle(ring[1], this.activeCorner === 'start')
    this.endCellFieldTarget.classList.toggle(ring[0], this.activeCorner === 'end')
    this.endCellFieldTarget.classList.toggle(ring[1], this.activeCorner === 'end')
  }

  // Typed corners. Validated here so an obvious typo is named immediately
  // rather than after a round trip.
  async cornersChanged(event) {
    if (event) event.preventDefault()

    // Enter fires keydown and then change, which would otherwise ask the same
    // question twice.
    if (this.paramsKey() === this.appliedParams) return

    const start = this.normalizeField(this.startCellFieldTarget)
    const end = this.normalizeField(this.endCellFieldTarget)

    const invalid = [start, end].find((value) => value && !this.constructor.CELL_PATTERN.test(value))
    if (invalid) {
      this.showError(`"${invalid}" is not a cell reference. Use a column letter and a row number, such as A3.`)
      return
    }

    // The top-left is what everything else is measured from, so a bottom-right
    // on its own is not yet a selection.
    if (!start) return

    await this.reload()
  }

  // Turning expansion off pins the bottom row at whatever the bottom-right cell
  // says; turning it on hands the row back to the file. Either way the server
  // re-resolves the range, so the summary always shows what would be read.
  async expandRowsChanged() {
    if (!this.normalizeField(this.startCellFieldTarget)) return

    await this.reload()
  }

  async cellClicked(params) {
    if (!params.colDef || params.colDef.field === '__row') return

    const reference = `${params.colDef.field}${params.data.__row}`

    // A bottom-right with no top-left describes nothing, so the first click is
    // always the anchor however the fields are focused.
    if (this.activeCorner === 'end' && this.normalizeField(this.startCellFieldTarget)) {
      this.endCellFieldTarget.value = reference
    } else {
      this.startCellFieldTarget.value = reference
      // A new top-left invalidates a bottom-right measured from the old one;
      // leaving it would silently re-anchor the user's range.
      this.endCellFieldTarget.value = ''
    }

    await this.reload()
  }

  // Everything the server needs to describe the current choice. Read from the
  // fields rather than kept in a parallel copy, so what is shown is what is
  // sent.
  currentParams() {
    return {
      sheet: this.sheetSelectTarget.value,
      start_cell: this.normalizeField(this.startCellFieldTarget),
      end_cell: this.normalizeField(this.endCellFieldTarget),
      expand_rows: this.expandRowsCheckboxTarget.checked
    }
  }

  paramsKey() {
    return JSON.stringify(this.currentParams())
  }

  async reload() {
    this.clearError()
    this.appliedParams = this.paramsKey()

    try {
      const response = await fetch(this.previewUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          s3_key: this.s3Key,
          original_filename: this.filename,
          ...this.currentParams()
        })
      })

      const payload = await response.json().catch(() => null)

      if (!response.ok) {
        // The server's message is already written for a person, and names the
        // specific problem (which cell is empty, which sheets do exist).
        this.showError((payload && payload.error) || 'This file could not be analysed.')
        return
      }

      this.applyPreview(payload)
    } catch (error) {
      console.error('Preview error:', error)
      this.showError('Could not reach the server to analyse this file.')
    }
  }

  applyPreview(preview) {
    this.selection = preview.selection || null

    // On an autoloaded page the sheet list arrives with the first response
    // rather than with an upload event.
    if (preview.sheets && this.sheetSelectTarget.options.length === 0) {
      this.renderSheets(preview)
    }
    this.buildGrid(preview)
    this.renderWarnings(preview.warnings || [])
    this.renderSummary(preview)
    this.syncFields()

    if (this.selection) {
      this.setHidden(this.selection)
      this.continueButtonTarget.disabled = false
    } else {
      this.setHidden(null)
      this.continueButtonTarget.disabled = true
    }
  }

  // Write the resolved corners back into the fields, so a corner that was
  // detected rather than typed is visible and editable, and so a bottom-right
  // row that follows the file shows the row it currently reaches.
  syncFields() {
    if (!this.selection) return

    this.startCellFieldTarget.value = this.selection.start_cell
    this.endCellFieldTarget.value = this.selection.end_cell
    // Once an anchor exists, the next click is far more likely to be aimed at
    // the other corner.
    this.setActiveCorner('end')
  }

  buildGrid(preview) {
    const { columns, rows } = preview.grid

    const columnDefs = [
      {
        field: '__row',
        headerName: '',
        width: 60,
        pinned: 'left',
        sortable: false,
        cellClass: 'text-xs text-gray-400 bg-gray-50 text-center'
      },
      ...columns.map((letter) => ({
        field: letter,
        headerName: letter,
        sortable: false,
        minWidth: 110,
        cellClassRules: {
          'bg-indigo-100': (params) => this.isSelected(params),
          'font-semibold': (params) => this.isHeaderCell(params)
        }
      }))
    ]

    if (this.grid) {
      this.grid.destroy()
    }

    this.grid = createGrid(this.gridTarget, {
      theme: spreadsheetTheme,
      columnDefs,
      rowData: rows,
      suppressCellFocus: false,
      defaultColDef: { resizable: true, suppressMovable: true },
      onCellClicked: (params) => this.cellClicked(params)
    })
  }

  // Highlight the rectangle the current selection covers, so the user can see
  // what they picked rather than having to trust a range string.
  isSelected(params) {
    if (!this.selection || !params.colDef || params.colDef.field === '__row') return false

    const column = this.columnIndex(params.colDef.field)
    const startColumn = this.columnIndex(this.selection.start_cell.replace(/\d+$/, ''))
    const endColumn = this.columnIndex(this.selection.end_column)
    const startRow = parseInt(this.selection.start_cell.replace(/^[A-Z]+/, ''), 10)
    // A pinned bottom row is part of the rectangle and has to be drawn, or the
    // highlight would claim rows the import will not read.
    const endRow = this.selection.end_row

    if (column < startColumn || column > endColumn) return false
    if (params.data.__row < startRow) return false

    return !endRow || params.data.__row <= endRow
  }

  isHeaderCell(params) {
    if (!this.selection || !this.isSelected(params)) return false

    return params.data.__row === parseInt(this.selection.start_cell.replace(/^[A-Z]+/, ''), 10)
  }

  // Bijective base 26: A=1 .. Z=26, AA=27. Matches CellRef on the server.
  columnIndex(letters) {
    return letters.split('').reduce((acc, char) => acc * 26 + (char.charCodeAt(0) - 64), 0)
  }

  renderSummary(preview) {
    const parts = []

    if (this.selection) {
      parts.push(`Importing <strong>${this.selection.resolved_range}</strong> from <strong>${preview.sheet}</strong>`)
      parts.push(`${this.selection.headers.length} columns: ${this.selection.headers.join(', ')}`)
      parts.push(this.selection.expand_rows
        ? 'Rows grow with each file'
        : `Rows fixed at ${this.selection.end_row}`)
      this.hintTarget.classList.add('hidden')
    } else {
      this.hintTarget.classList.remove('hidden')
    }

    if (preview.grid.truncated_rows) {
      parts.push(`Showing the first ${preview.grid.rows.length} rows of ${preview.extent.last_row}.`)
    }

    this.summaryTarget.innerHTML = parts.join(' &middot; ')
  }

  renderWarnings(warnings) {
    if (warnings.length === 0) {
      this.warningTarget.classList.add('hidden')
      return
    }

    this.warningMessageTarget.textContent = warnings.map((w) => w.message).join(' ')
    this.warningTarget.classList.remove('hidden')
  }

  // The chosen corners ride along with the form so they can be persisted on the
  // mapping. end_row is posted only when the user pinned it: an absent end_row
  // is what tells the import to resolve the bottom row from whichever file it
  // is given, so a later file with more rows still imports in full.
  setHidden(selection) {
    const inputs = [
      [this.sheetInputTarget, selection ? this.sheetSelectTarget.value : ''],
      [this.startCellInputTarget, selection ? selection.start_cell : ''],
      [this.endColumnInputTarget, selection ? selection.end_column : ''],
      [this.endRowInputTarget, selection && selection.end_row ? String(selection.end_row) : ''],
      [this.formatInputTarget, selection ? 'xlsx' : '']
    ]

    inputs.forEach(([input, value]) => {
      input.value = value
      // Disabled inputs post nothing, which is what keeps a CSV upload from
      // being stamped with these fields just because the markup is present,
      // and what keeps an expanding range from posting a bottom row.
      input.disabled = value === ''
    })
  }

  continueWithSelection(event) {
    event.preventDefault()
    if (!this.selection) return

    this.continueButtonTarget.disabled = true
    this.continueButtonTarget.textContent = 'Analysing…'

    if (this.continueUrlValue) {
      const url = new URL(this.continueUrlValue, window.location.origin)
      url.searchParams.set('sheet_name', this.sheetSelectTarget.value)
      url.searchParams.set('start_cell', this.selection.start_cell)
      url.searchParams.set('end_column', this.selection.end_column)
      if (this.selection.end_row) url.searchParams.set('end_row', this.selection.end_row)
      url.searchParams.set('file_format', 'xlsx')
      window.location.assign(url.toString())
      return
    }

    // The controller sits on the wrapper, not the form, so find the form rather
    // than assuming this.element is submittable.
    const form = this.element.querySelector('form') || this.element.closest('form')
    // The upload controller already marked the upload complete, so this submits
    // straight through to Rails.
    form.requestSubmit()
  }

  normalizeField(field) {
    const value = field.value.trim().toUpperCase()
    field.value = value
    return value
  }

  showError(message) {
    // Let the same question be asked again: the params have not changed, but
    // the answer failed, so the dedupe must not swallow a retry.
    this.appliedParams = null
    this.errorMessageTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
    this.continueButtonTarget.disabled = true
  }

  clearError() {
    this.errorTarget.classList.add('hidden')
  }
}
